/* eslint-disable react-hooks/rules-of-hooks */
/* eslint-disable @typescript-eslint/no-unsafe-function-type */

import { ComponentLibrary } from '@suntex.classic/adobe-animate';
import { Logger } from '@suntex.core/logging';
import { bound, isInt } from '@suntex.core/math';

// Private static properties
let _tweeningEnabled = true;
const _delays: Record<number, Function> = {};
const _timers: Record<string, number> = {};
const _scopedDelays: WeakMap<object, Set<number>> = new WeakMap();
// Separate map for symbol scopes since WeakMap doesn't support symbols
const _symbolScopedDelays: Map<symbol, Set<number>> = new Map();

export type Ease =
  | 'linear'
  | 'easeIn'
  | 'easeInOut'
  | 'easeOut'
  | 'strongIn'
  | 'strongInOut'
  | 'strongOut'
  | 'elasticIn'
  | 'elasticInOut'
  | 'elasticOut'
  | 'bounceIn'
  | 'bounceInOut'
  | 'bounceOut'
  | 'backIn'
  | 'backInOut'
  | 'backOut';

/**
 * Navigate to a URL
 */
export function navigate(href: string): void {
  window.location.href = href;
}

/**
 * Set the frame of a movie clip
 */
export function setFrame(
  movieClip: createjs.DisplayObject | null | undefined,
  frame: number | string,
): unknown {
  if (movieClip) {
    if (isMovieClip(movieClip)) {
      const newFrame = bound(
        parseInt(frame as string),
        1,
        movieClip.timeline.duration,
      );
      if (isInt(newFrame)) movieClip.gotoAndStop(newFrame - 1);
      else movieClip.gotoAndStop(frame);
    }
  }
  return movieClip;
}

function isMovieClip(
  obj: createjs.DisplayObject | null | undefined,
): obj is createjs.MovieClip {
  return !!(obj && (obj as createjs.MovieClip)['timeline']);
}

type EaseType =
  | 'linear'
  | 'easeIn'
  | 'easeInOut'
  | 'easeOut'
  | 'strongIn'
  | 'strongInOut'
  | 'strongOut'
  | 'elasticIn'
  | 'elasticInOut'
  | 'elasticOut'
  | 'bounceIn'
  | 'bounceInOut'
  | 'bounceOut'
  | 'backIn'
  | 'backInOut'
  | 'backOut';

/**
 * Tween a property of an object
 */
export function tween<T extends object, K extends keyof T, A extends unknown[]>(
  obj: T | null | undefined,
  prop: K,
  target: T[K],
  ms?: number,
  ease?: EaseType,
  onComplete?: (...args: A) => void,
  callbackArgs?: A,
): unknown {
  if (obj === undefined) {
    return;
  }
  if (obj !== null) {
    if (obj[prop] == target) {
      if (onComplete != null) delay(onComplete, ms, callbackArgs);
      return;
    } else if (ms == 0 || !_tweeningEnabled) {
      obj[prop] = target;
      if (onComplete != null)
        onComplete(...(callbackArgs ?? ([] as unknown[] as A)));
    } else {
      const props: Partial<T> = {};
      props[prop] = target;
      let easeFunction;

      switch (ease) {
        case 'linear':
          easeFunction = createjs.Ease.linear;
          break;

        case 'easeIn':
          easeFunction = createjs.Ease.quadIn;
          break;
        case 'easeInOut':
          easeFunction = createjs.Ease.quadInOut;
          break;
        case 'easeOut':
          easeFunction = createjs.Ease.quadOut;
          break;

        case 'strongIn':
          easeFunction = createjs.Ease.quartIn;
          break;
        case 'strongInOut':
          easeFunction = createjs.Ease.quartInOut;
          break;
        case 'strongOut':
          easeFunction = createjs.Ease.quartOut;
          break;

        case 'elasticIn':
          easeFunction = createjs.Ease.elasticIn;
          break;
        case 'elasticInOut':
          easeFunction = createjs.Ease.elasticInOut;
          break;
        case 'elasticOut':
          easeFunction = createjs.Ease.elasticOut;
          break;

        case 'bounceIn':
          easeFunction = createjs.Ease.bounceIn;
          break;
        case 'bounceInOut':
          easeFunction = createjs.Ease.bounceInOut;
          break;
        case 'bounceOut':
          easeFunction = createjs.Ease.bounceOut;
          break;

        case 'backIn':
          easeFunction = createjs.Ease.backIn;
          break;
        case 'backInOut':
          easeFunction = createjs.Ease.backInOut;
          break;
        case 'backOut':
          easeFunction = createjs.Ease.backOut;
          break;

        default:
          easeFunction = createjs.Ease.quadInOut;
          break;
      }

      return createjs.Tween.get(obj)
        .to(props, ms, easeFunction)
        .call(function () {
          if (onComplete != null) {
            onComplete(...(callbackArgs ?? ([] as unknown[] as A)));
          }
        });
    }
  }
}

export interface DelayOptions {
  /** The delay in milliseconds */
  ms?: number;
  /** Arguments to pass to the function */
  args?: unknown[];
  /** The `this` context for the function */
  thisArg?: unknown;
  /** Scope object/symbol for grouped cancellation via cancelDelaysForScope() */
  scope?: object | symbol;
}

/**
 * Create a delayed version of a function that can be scheduled.
 * Returns a function that when called, schedules the delay and returns the timer ID.
 * @param func The function to call after the delay
 * @param options Delay options including ms, args, thisArg, and scope
 * @returns A function that schedules the delay and returns the timer ID
 */
export function withDelay(
  func: Function,
  options: DelayOptions = {},
): () => number {
  const { ms, args, thisArg = null, scope } = options;

  return () => {
    const timerID = window.setTimeout(function () {
      func.apply(thisArg, args);
      delete _delays[timerID];
      if (scope) {
        if (typeof scope === 'symbol') {
          const scopeSet = _symbolScopedDelays.get(scope);
          scopeSet?.delete(timerID);
          // Clean up empty symbol scope sets to prevent accumulation
          if (scopeSet && scopeSet.size === 0) {
            _symbolScopedDelays.delete(scope);
          }
        } else {
          _scopedDelays.get(scope)?.delete(timerID);
        }
      }
    }, ms);
    _delays[timerID] = func;

    if (scope) {
      if (typeof scope === 'symbol') {
        let scopeSet = _symbolScopedDelays.get(scope);
        if (!scopeSet) {
          scopeSet = new Set();
          _symbolScopedDelays.set(scope, scopeSet);
        }
        scopeSet.add(timerID);
      } else {
        let scopeSet = _scopedDelays.get(scope);
        if (!scopeSet) {
          scopeSet = new Set();
          _scopedDelays.set(scope, scopeSet);
        }
        scopeSet.add(timerID);
      }
    }

    return timerID;
  };
}

/**
 * Delay a function call
 * @param func The function to call after the delay
 * @param ms The delay in milliseconds
 * @param args Arguments to pass to the function
 * @param thisArg The `this` context for the function
 * @returns The timer ID (can be used with clearTimeout if needed)
 */
export function delay(
  func: Function,
  ms?: number,
  args?: unknown[],
  thisArg?: unknown,
): number {
  return withDelay(func, { ms, args, thisArg })();
}

/**
 * Cancel a delayed function
 */
export function cancelDelay(func: Function): void {
  for (const timerID in _delays) {
    if (_delays[timerID] === func) {
      clearTimeout(Number(timerID));
      delete _delays[timerID];
    }
  }
}

/**
 * Cancel all delayed functions
 */
export function cancelAllDelays(): void {
  for (const timerID in _delays) {
    clearTimeout(Number(timerID));
    delete _delays[timerID];
  }
  // Clear symbol scope tracking (WeakMap entries will be GC'd automatically)
  _symbolScopedDelays.clear();
}

/**
 * Cancel all delays registered with a specific scope object or symbol
 * @param scope The scope object/symbol whose delays should be cancelled
 */
export function cancelDelaysForScope(scope: object | symbol): void {
  const scopeSet =
    typeof scope === 'symbol'
      ? _symbolScopedDelays.get(scope)
      : _scopedDelays.get(scope);
  if (!scopeSet) return;

  for (const timerID of scopeSet) {
    clearTimeout(timerID);
    delete _delays[timerID];
  }
  scopeSet.clear();

  // Remove empty scope set from symbol map to prevent accumulation
  if (typeof scope === 'symbol') {
    _symbolScopedDelays.delete(scope);
  }
  // Note: WeakMap entries for object scopes will be GC'd automatically
}

/**
 * Remove all tweens on a specific object
 * @param target The object whose tweens should be removed
 */
export function removeObjectTweens(target: object): void {
  createjs.Tween.removeTweens(target);
}

/**
 * Stop all tweens and delays
 */
export function stopAllTweensAndDelays(): void {
  createjs.Tween.removeAllTweens();
  cancelAllDelays();
}

/**
 * Disable animation
 */
export function disableAnimation(): void {
  _tweeningEnabled = false;
}

/* Mouse Interaction */

/**
 * Enable mouse interaction on a display object
 */
export function enable(o: createjs.DisplayObject): void {
  o.mouseEnabled = true;
}

/**
 * Disable mouse interaction on a display object
 */
export function disable(o: createjs.DisplayObject): void {
  o.mouseEnabled = false;
}

/**
 * Add a click event listener to a movie clip
 */
export function onClick<T extends createjs.DisplayObject>(
  movieClip: T,
  onClick: (eventObj: TargettedEvent<T>) => boolean | void,
): void {
  movieClip.addEventListener(
    'click',
    onClick as (obj: object) => boolean | void,
  );
}

/**
 * Add a mouse over event listener to a movie clip
 */
export function onMouseOver<T extends createjs.DisplayObject>(
  movieClip: T,
  onMouseOver: (eventObj: TargettedEvent<T>) => boolean | void,
): void {
  movieClip.addEventListener(
    'mouseover',
    onMouseOver as (obj: object) => boolean | void,
  );
}

/**
 * Add a mouse out event listener to a movie clip
 */
export function onMouseOut<T extends createjs.DisplayObject>(
  movieClip: T,
  onMouseOut: (eventObj: TargettedEvent<T>) => boolean | void,
): void {
  movieClip.addEventListener(
    'mouseout',
    onMouseOut as (obj: object) => boolean | void,
  );
}

export function createLink(movieClip: createjs.DisplayObject, url: string) {
  useHandCursor(movieClip, true);
  onClick(movieClip, () => {
    navigate(url);
  });
}

/* Visual */

/**
 * Hide a display object
 */
export function hide(
  o: createjs.DisplayObject | null | undefined,
  ms?: number,
  ease?: EaseType,
): void {
  if (o == null) {
    Logger.for('hide').warn('Object is not defined');
    return;
  }

  if (ms == null) ms = 0;

  if (ms <= 0) {
    o.alpha = 0.0;
    o.visible = false;
  } else {
    tween(o, 'alpha', 0.0, ms, ease, () => {
      o.visible = false;
    });
  }
}

/**
 * Show a display object
 */
export function show(
  o: createjs.DisplayObject | null | undefined,
  ms?: number,
  ease?: EaseType,
): void {
  if (o == null) {
    Logger.for('show').warn('Object is not defined');
    return;
  }

  if (ms == null) ms = 0;

  o.visible = true;

  if (ms <= 0) {
    o.alpha = 1.0;
  } else {
    tween(o, 'alpha', 1.0, ms, ease);
  }
}

/**
 * Tween a display object's alpha to any target value.
 * Ensures the object is visible before tweening.
 */
export function fadeTo(
  o: createjs.DisplayObject | null | undefined,
  alpha: number,
  ms?: number,
  ease?: EaseType,
): void {
  if (o == null) return;
  const wasHidden = !o.visible;
  o.visible = true;
  if (!ms || ms <= 0) {
    o.alpha = alpha;
  } else {
    if (wasHidden) o.alpha = 0;
    tween(o, 'alpha', alpha, ms, ease);
  }
}

/**
 * Convert a stage-space point to the local coordinate space of a display object.
 * Thin wrapper around createjs's built-in globalToLocal.
 */
export function globalToLocal(
  movieClip: createjs.DisplayObject,
  x: number,
  y: number,
): createjs.Point {
  return movieClip.globalToLocal(x, y);
}

/**
 * Bring a display object to the front of its parent
 */
export function bringToFront(mc: createjs.DisplayObject, place = 1): void {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (mc.parent) {
    mc.parent.setChildIndex(mc, mc.parent.children.length - place);
  }
}
export function createButton<T extends { value?: unknown }>(
  movieClip: createjs.DisplayObject & T,
  clickHandler: (eventObj: TargettedEvent<T>) => boolean | void,
  value?: T['value'],
  onRollOver?: (eventObj: TargettedEvent<T>) => boolean | void,
  onRollOut?: (eventObj: TargettedEvent<T>) => boolean | void,
) {
  useHandCursor(movieClip);
  onClick(movieClip, clickHandler);
  if (onRollOver != null) onMouseOver(movieClip, onRollOver);
  if (onRollOut != null) onMouseOut(movieClip, onRollOut);
  if (value != null) movieClip['value'] = value;
}

/**
 * Create a "simple" button that swaps to frame 2 on hover and back to
 * frame 1 on rollout, mirroring the legacy `UIUtil.createSimpleButton`.
 */
export function createSimpleButton<T extends { value?: unknown }>(
  movieClip: createjs.DisplayObject & T,
  clickHandler: (eventObj: TargettedEvent<T>) => boolean | void,
  value?: T['value'],
): void {
  onClick(movieClip, clickHandler);
  if (value != null) movieClip['value'] = value;
  onMouseOver(movieClip, (e) => {
    setFrame(getEventTarget(e), 2);
  });
  onMouseOut(movieClip, (e) => {
    setFrame(getEventTarget(e), 1);
  });
  setFrame(movieClip, 1);
}

export function setTimeout(handler: Function, timeout?: number): number {
  return window.setTimeout(handler, timeout ?? 0);
}

export function setInterval(
  handler: Function & { name: string },
  timeout?: number,
) {
  // Clear any existing interval registered under the same name first, otherwise
  // the previous interval leaks (its id is overwritten) and keeps ticking.
  clearInterval(handler);
  _timers[handler.name] = window.setInterval(handler, timeout ?? 0);
}

export function clearInterval(handler: Function & { name: string }) {
  window.clearInterval(_timers[handler.name]);
  delete _timers[handler.name];
}

export function clearAllIntervals(): void {
  for (const name in _timers) {
    window.clearInterval(_timers[name]);
    delete _timers[name];
  }
}

export type TargettedEvent<T> = {
  currentTarget: createjs.DisplayObject & T;
};

export function getEventTarget<T>(e: TargettedEvent<T>) {
  return e.currentTarget;
}

/*
 * Keyboard
 */

// Maps original handlers to wrapped handlers so clearKeyDown can remove the correct listener
const _keyHandlerMap = new WeakMap<Function, (event: KeyboardEvent) => void>();

export function setKeyDown(handler: (event: KeyboardEvent) => void) {
  // Ensure repeated registration of the same handler is a no-op.
  if (_keyHandlerMap.has(handler)) return;

  function wrapped(e: KeyboardEvent) {
    if (e.repeat) return;
    handler(e);
  }
  _keyHandlerMap.set(handler, wrapped);
  document.addEventListener('keydown', wrapped);
}

export function clearKeyDown(handler: (event: KeyboardEvent) => void) {
  const wrapped = _keyHandlerMap.get(handler);
  if (wrapped) {
    document.removeEventListener('keydown', wrapped);
    _keyHandlerMap.delete(handler);
  }
}

export const KeyCodes = {
  BACKSPACE: 8,
  ENTER: 13,
  SPACE: 32,
  LEFT: 37,
  UP: 38,
  RIGHT: 39,
  DOWN: 40,
  DELETE: 46,

  Key0: 48,
  Key1: 49,
  Key2: 50,
  Key3: 51,
  Key4: 52,

  Numpad0: 96,
  Numpad1: 97,
  Numpad2: 98,
  Numpad3: 99,
  Numpad4: 100,
  Numpad5: 101,
  Numpad6: 102,
  Numpad7: 103,
  Numpad8: 104,
  Numpad9: 105,
};

/**
 * Set the cursor style for a movie clip
 */
export function useHandCursor(
  movieClip: createjs.DisplayObject,
  handCursor = true,
): void {
  if (handCursor) {
    movieClip.cursor = 'pointer';
  } else {
    movieClip.cursor = 'default';
  }
}

export function scaleTo(
  o: createjs.DisplayObject,
  scale: number,
  ms = 0,
  ease?: Ease,
) {
  if (ms <= 0) {
    o.scaleX = o.scaleY = scale;
  } else {
    tween(o, 'scaleX', scale, ms, ease);
    tween(o, 'scaleY', scale, ms, ease);
  }
}

export function setRegistrationPoint(
  o: createjs.DisplayObject,
  x: number,
  y: number,
): void {
  o.x = o.x - (o.regX - x);
  o.y = o.y - (o.regY - y);
  o.regX = x;
  o.regY = y;
}

/**
 * Instantiate a component from a library by name with runtime validation
 * @param lib - The component library
 * @param componentName - The name of the component to instantiate
 * @returns A new instance of the component
 * @throws Error if the component is not found in the library
 */
export function createDisplayObject<T extends createjs.DisplayObject>(
  lib: ComponentLibrary,
  componentName: string,
): T {
  const Constructor = lib[componentName];
  if (!Constructor) {
    throw new Error(`Component "${componentName}" not found in library`);
  }
  return new Constructor() as T;
}

/* ----------------------------------------------------------------------
 * Helpers ported from `@suntex.classic/utils` UIUtil for the modern
 * `@suntex.modules/*` programs. Same semantics as the legacy globals,
 * with stricter typing.
 * ------------------------------------------------------------------- */

const PULSE_MS_DIVISOR = 2;
const TWEEN_TO_DEFAULT_MS = 800;

let _activeLib: ComponentLibrary | null = null;

/**
 * @deprecated
 *
 * Set the active component library used by {@link createNew}. Modules call
 * this once during init (typically from the `ModuleView` `onReady` callback)
 * so that gameplay code can instantiate FLA symbols by name without
 * threading the lib through every call site.
 */
export function setLib(lib: ComponentLibrary): void {
  _activeLib = lib;
}

/**
 * @deprecated
 *
 * Instantiate an FLA symbol by name from the library configured via
 * {@link setLib}. Mirrors the legacy `UIUtil.createNew('Foo')` global.
 */
export function createNew<
  T extends createjs.DisplayObject = createjs.DisplayObject,
>(componentName: string): T {
  if (!_activeLib) {
    throw new Error(
      `UIUtil.setLib(lib) must be called before createNew('${componentName}')`,
    );
  }
  return createDisplayObject<T>(_activeLib, componentName);
}

/**
 * Get the current frame number (1-based) of a movie clip.
 */
export function getCurrentFrame(
  movieClip: createjs.DisplayObject | null | undefined,
): number {
  if (!movieClip || !isMovieClip(movieClip)) return 1;
  return (movieClip.timeline.position as number) + 1;
}

/**
 * Move a display object to (x, y), optionally tweening over `ms` ms.
 */
export function moveTo(
  o: createjs.DisplayObject,
  x: number,
  y: number,
  ms?: number,
  ease?: Ease,
): void {
  tween(o, 'x', x, ms, ease);
  tween(o, 'y', y, ms, ease);
}

/**
 * Tween position, scale, and alpha simultaneously. Mirrors the legacy
 * `UIUtil.tweenTo` overload used heavily in the classic modules.
 */
export function tweenTo(
  o: createjs.DisplayObject,
  x: number,
  y: number,
  scale = 1.0,
  alpha = 1.0,
  ms = TWEEN_TO_DEFAULT_MS,
  ease: Ease = 'easeInOut',
): void {
  tween(o, 'x', x, ms, ease);
  tween(o, 'y', y, ms, ease);
  tween(o, 'scaleX', scale, ms, ease);
  tween(o, 'scaleY', scale, ms, ease);
  tween(o, 'alpha', alpha, ms, ease);
}

/**
 * Pulse a property of an object out and back. The default property is
 * `alpha` and the default target is `1.0`.
 */
export function pulse(
  obj: createjs.DisplayObject | null | undefined,
  ms: number,
  target = 1.0,
  prop: 'alpha' | 'scaleX' | 'scaleY' = 'alpha',
): void {
  if (obj == null) return;
  const original = (obj as unknown as Record<string, number>)[prop]!;
  tween(obj, prop, target, ms / PULSE_MS_DIVISOR, 'easeInOut', () => {
    tween(obj, prop, original, ms / PULSE_MS_DIVISOR, 'easeInOut');
  });
}

/**
 * Send a display object behind its siblings.
 */
export function sendToBack(mc: createjs.DisplayObject, place = 0): void {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (mc.parent) {
    mc.parent.setChildIndex(mc, place);
  }
}

/**
 * Read a mouse-event position, optionally translated into a movie clip's
 * local coordinate space.
 */
export function getMousePosition(
  e: createjs.MouseEvent,
  movieClip?: createjs.DisplayObject,
): createjs.Point {
  if (movieClip != null) return globalToLocal(movieClip, e.stageX, e.stageY);
  return new createjs.Point(e.stageX, e.stageY);
}

/**
 * Wire `mousedown` and `pressup` listeners on a single display object.
 */
export function onMouseDownAndUp<T extends createjs.DisplayObject>(
  movieClip: T,
  onDown: ((e: createjs.MouseEvent) => void) | null,
  onUp: ((e: createjs.MouseEvent) => void) | null,
): void {
  if (onDown != null) {
    movieClip.addEventListener(
      'mousedown',
      onDown as (obj: object) => boolean | void,
    );
  }
  if (onUp != null) {
    movieClip.addEventListener(
      'pressup',
      onUp as (obj: object) => boolean | void,
    );
  }
}

/**
 * Wire `mousedown`, `pressmove`, and `pressup` listeners on a single
 * display object.
 */
export function onMouseMove<T extends createjs.DisplayObject>(
  movieClip: T,
  onDown: ((e: createjs.MouseEvent) => void) | null,
  onMove: ((e: createjs.MouseEvent) => void) | null,
  onUp: ((e: createjs.MouseEvent) => void) | null,
): void {
  if (onDown != null) {
    movieClip.addEventListener(
      'mousedown',
      onDown as (obj: object) => boolean | void,
    );
  }
  if (onMove != null) {
    movieClip.addEventListener(
      'pressmove',
      onMove as (obj: object) => boolean | void,
    );
  }
  if (onUp != null) {
    movieClip.addEventListener(
      'pressup',
      onUp as (obj: object) => boolean | void,
    );
  }
}

/**
 * A createjs DisplayObject that has been initialised as a two-state toggle
 * button via `createSimpleToggleButton`.
 */
export interface ToggleButton extends createjs.DisplayObject {
  /** `true` = button is in the "on" state, `false` = "off" state. */
  value: boolean;
  /** Switch to the "on" state (frame 1) without firing the toggle callback. */
  toggleOn(): void;
  /** Switch to the "off" state (frame 3) without firing the toggle callback. */
  toggleOff(): void;
}

// Frame indices for the four-frame toggle button clip
const TOGGLE_FRAME_ON = 1;
const TOGGLE_FRAME_ON_HOVER = 2;
const TOGGLE_FRAME_OFF = 3;
const TOGGLE_FRAME_OFF_HOVER = 4;

/**
 * Convert a four-frame movie clip into a two-state toggle button, mirroring
 * the legacy `UIUtil.createSimpleToggleButton`.
 *
 * Frame mapping: 1 = on, 2 = on-hover, 3 = off, 4 = off-hover.
 *
 * The movie clip is augmented in-place with `value`, `toggleOn()`, and
 * `toggleOff()` properties.  `onToggleFunc` is called with the new boolean
 * value and the button reference on each click.
 */
export function createSimpleToggleButton(
  movieClip: createjs.DisplayObject,
  onToggleFunc: (value: boolean, btn: ToggleButton) => void,
  initialValue = true,
): ToggleButton {
  useHandCursor(movieClip);

  const btn = movieClip as unknown as ToggleButton;
  btn.value = initialValue;

  onClick(movieClip, (e) => {
    const mc = getEventTarget(e) as unknown as ToggleButton;
    mc.value = !mc.value;
    setFrame(mc, mc.value ? TOGGLE_FRAME_ON : TOGGLE_FRAME_OFF);
    onToggleFunc(mc.value, mc);
  });

  onMouseOver(movieClip, (e) => {
    const mc = getEventTarget(e) as unknown as ToggleButton;
    setFrame(mc, mc.value ? TOGGLE_FRAME_ON_HOVER : TOGGLE_FRAME_OFF_HOVER);
  });

  onMouseOut(movieClip, (e) => {
    const mc = getEventTarget(e) as unknown as ToggleButton;
    setFrame(mc, mc.value ? TOGGLE_FRAME_ON : TOGGLE_FRAME_OFF);
  });

  btn.toggleOn = () => {
    btn.value = true;
    setFrame(movieClip, TOGGLE_FRAME_ON);
  };

  btn.toggleOff = () => {
    btn.value = false;
    setFrame(movieClip, TOGGLE_FRAME_OFF);
  };

  setFrame(movieClip, initialValue ? TOGGLE_FRAME_ON : TOGGLE_FRAME_OFF);

  return btn;
}
